import cx from "classnames";
import React, {
    type ChangeEvent,
    type KeyboardEventHandler,
    type MouseEventHandler,
    useEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { DeletePastVoteWindow } from "@/appInterface/windows/DeletePastVoteWindow";
import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import { CloseIcon } from "@/icons";
import { getDate } from "@/utils";
import style from "./pastVoteListItem.module.css";

type PastVoteListItemProps = {
    vote: VoteEntity;
    onRename: (title: string) => void;
    onRemove: () => void;
    onClick: (val: boolean, vote?: VoteEntity) => void;
    openId: string;
    setOpenId: (id: string) => void;
    renameId: string;
    setRenameId: (id: string) => void;
};

function useOutsideClick<T extends HTMLElement>(callback: () => void) {
    const ref = useRef<T>(null);

    useEffect(() => {
        function handleClick(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback();
            }
        }

        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [callback]);

    return ref;
}

export function PastVoteListItem({
    vote,
    onRemove,
    onRename,
    onClick,
    openId,
    setOpenId,
    renameId,
    setRenameId,
}: PastVoteListItemProps) {
    const [activeMoreActions, setActiveMoreActions] = useState(false);
    const [voteTitle, setVoteTitle] = useState(() => vote.text);
    const [showDeleteWindow, setShowDeleteWindow] = useState(false);
    const [error, setError] = useState("");

    const isOpen = openId === vote.UID;
    const menuRef = useOutsideClick<HTMLDivElement>(() => {
        if (isOpen) setOpenId("");
    });

    const handleCloseDeleteWindow = () => {
        setShowDeleteWindow(false);
    };

    const handleDelete = () => {
        onRemove();
        handleCloseDeleteWindow();
    };

    const handleRename = (e: ChangeEvent<HTMLInputElement>) => {
        setVoteTitle(e.target.value);
        setOpenId("");
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.code === "Tab" || e.code === "Enter") {
            if (!error) {
                onRename(voteTitle.trim());
                setRenameId("");
            }
        }
    };

    const handleCloseToRename: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();
        setVoteTitle(vote.text);
        setRenameId("");
    };

    const onFocus = () => {
        canv.appInterface.inputFocused = 1;
    };
    const onBlur = () => {
        canv.appInterface.inputFocused = 0;
    };

    const handleClick = () => onClick(true, vote);
    const canEditOrRename = canv.isBoardModeEditOrOwner();
    const isVoteOwner =
        canv.isBoardModeOwner() || vote.creatorToken === canv.getInitDataProp("userToken");

    useEffect(() => {
        if (voteTitle.trim().length < 3 || voteTitle.trim().length > 100) {
            setError("Название должно быть от 3 до 100 символов");
        } else {
            setError("");
        }
    }, [voteTitle]);

    return (
        <div
            className={cx(style.pastVoteListItem, { [style.rename]: renameId })}
            key={vote.UID}
            onMouseDown={renameId || activeMoreActions ? undefined : handleClick}
        >
            {showDeleteWindow &&
                createPortal(
                    <DeletePastVoteWindow
                        name={vote.text}
                        onClose={handleCloseDeleteWindow}
                        onDelete={handleDelete}
                    />,
                    document.body,
                )}
            {renameId === vote.UID ? (
                <div className={`baseInputWrapper ${error && "error"}`}>
                    <input
                        id={`${vote.UID}baseInput`}
                        maxLength={100}
                        value={voteTitle}
                        onInput={handleRename}
                        onKeyDown={handleKeyDown}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        className="baseInput"
                        type="text"
                    />
                    <label className="baseInputLabel" htmlFor={`${vote.UID}baseInput`}>
                        Название
                    </label>
                    <button
                        className="baseInputButton"
                        onMouseDown={handleCloseToRename}
                        type="button"
                    >
                        <CloseIcon className={style.closeIcon} />
                    </button>
                    <span className="baseInputError">{error}</span>
                </div>
            ) : (
                <>
                    <div className={style.pasVoteListItemTitleWrapper}>
                        <p className={style.pasVoteListItemTitle}>{vote.text}</p>
                        {canEditOrRename && (
                            <div
                                className={style.voteMoreActionsButton}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    setOpenId(vote.UID);
                                }}
                            >
                                {isOpen && (
                                    <div className={style.voteActionsModal} ref={menuRef}>
                                        <div
                                            className={cx(
                                                style.voteActionItem,
                                                style.voteActionCopyLink,
                                            )}
                                            onMouseDown={() => setRenameId(vote.UID)}
                                        >
                                            Переименовать
                                        </div>
                                        {isVoteOwner && (
                                            <div
                                                className={cx(
                                                    style.voteActionItem,
                                                    style.voteActionDelete,
                                                )}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    setShowDeleteWindow(true);
                                                    setActiveMoreActions((prev) => !prev);
                                                }}
                                            >
                                                Удалить
                                            </div>
                                        )}
                                    </div>
                                )}

                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8.00033 8.66659C8.36852 8.66659 8.66699 8.36811 8.66699 7.99992C8.66699 7.63173 8.36852 7.33325 8.00033 7.33325C7.63214 7.33325 7.33366 7.63173 7.33366 7.99992C7.33366 8.36811 7.63214 8.66659 8.00033 8.66659Z"
                                        fill="#2C2D32"
                                    />
                                    <path
                                        d="M12.667 8.66659C13.0352 8.66659 13.3337 8.36811 13.3337 7.99992C13.3337 7.63173 13.0352 7.33325 12.667 7.33325C12.2988 7.33325 12.0003 7.63173 12.0003 7.99992C12.0003 8.36811 12.2988 8.66659 12.667 8.66659Z"
                                        fill="#2C2D32"
                                    />
                                    <path
                                        d="M3.33366 8.66659C3.70185 8.66659 4.00033 8.36811 4.00033 7.99992C4.00033 7.63173 3.70185 7.33325 3.33366 7.33325C2.96547 7.33325 2.66699 7.63173 2.66699 7.99992C2.66699 8.36811 2.96547 8.66659 3.33366 8.66659Z"
                                        fill="#2C2D32"
                                    />
                                    <path
                                        d="M8.00033 8.66659C8.36852 8.66659 8.66699 8.36811 8.66699 7.99992C8.66699 7.63173 8.36852 7.33325 8.00033 7.33325C7.63214 7.33325 7.33366 7.63173 7.33366 7.99992C7.33366 8.36811 7.63214 8.66659 8.00033 8.66659Z"
                                        stroke="#2C2D32"
                                        stroke-width="0.866667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M12.667 8.66659C13.0352 8.66659 13.3337 8.36811 13.3337 7.99992C13.3337 7.63173 13.0352 7.33325 12.667 7.33325C12.2988 7.33325 12.0003 7.63173 12.0003 7.99992C12.0003 8.36811 12.2988 8.66659 12.667 8.66659Z"
                                        stroke="#2C2D32"
                                        stroke-width="0.866667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M3.33366 8.66659C3.70185 8.66659 4.00033 8.36811 4.00033 7.99992C4.00033 7.63173 3.70185 7.33325 3.33366 7.33325C2.96547 7.33325 2.66699 7.63173 2.66699 7.99992C2.66699 8.36811 2.96547 8.66659 3.33366 8.66659Z"
                                        stroke="#2C2D32"
                                        stroke-width="0.866667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>
                    <div className={style.pasVoteListItemDateAndActorWrapper}>
                        <p className={style.pasVoteListItemDateAndActor}>
                            {`${getDate(new Date(vote.start)).replace(",", " в ")}, ${vote.creatorName}`}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
